import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  ConsentStore,
  CookieConsentPreferences,
  CONSENT_STORAGE_KEY,
  CURRENT_CONSENT_VERSION,
} from '@/types';

const DEFAULT_PREFERENCES: CookieConsentPreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  consentGiven: false,
  consentTimestamp: null,
  consentVersion: CURRENT_CONSENT_VERSION,
};

export const useConsentStore = create<ConsentStore>()(
  persist(
    (set, get) => ({
      // State
      preferences: DEFAULT_PREFERENCES,
      isBannerOpen: false,
      isSettingsOpen: false,
      consentVersion: CURRENT_CONSENT_VERSION,

      // Actions
      acceptAll: () => {
        const timestamp = new Date().toISOString();
        set({
          preferences: {
            ...DEFAULT_PREFERENCES,
            functional: true,
            analytics: true,
            marketing: true,
            consentGiven: true,
            consentTimestamp: timestamp,
            consentVersion: CURRENT_CONSENT_VERSION,
          },
          isBannerOpen: false,
          isSettingsOpen: false,
        });
      },

      rejectAll: () => {
        const timestamp = new Date().toISOString();
        set({
          preferences: {
            ...DEFAULT_PREFERENCES,
            // necessary is always true from DEFAULT_PREFERENCES
            functional: false,
            analytics: false,
            marketing: false,
            consentGiven: true,
            consentTimestamp: timestamp,
            consentVersion: CURRENT_CONSENT_VERSION,
          },
          isBannerOpen: false,
          isSettingsOpen: false,
        });
      },

      savePreferences: (newPreferences) => {
        const timestamp = new Date().toISOString();
        const currentPreferences = get().preferences;
        
        set({
          preferences: {
            ...currentPreferences,
            ...newPreferences,
            necessary: true, // Always enforce necessary
            consentGiven: true,
            consentTimestamp: timestamp,
            consentVersion: CURRENT_CONSENT_VERSION,
          },
          isBannerOpen: false,
          isSettingsOpen: false,
        });
      },

      openSettings: () => {
        set({ isSettingsOpen: true });
      },

      closeSettings: () => {
        set({ isSettingsOpen: false });
      },

      resetConsent: () => {
        set({
          preferences: DEFAULT_PREFERENCES,
          isBannerOpen: true,
          isSettingsOpen: false,
        });
      },
    }),
    {
      name: CONSENT_STORAGE_KEY,
      partialize: (state) => ({
        preferences: state.preferences,
        consentVersion: state.consentVersion,
      }),
      // Handle version changes or migration if needed
      onRehydrateStorage: () => (state) => {
        if (state) {
          // If stored version doesn't match current version, reset consent
          if (state.preferences.consentVersion !== CURRENT_CONSENT_VERSION) {
            state.resetConsent();
          } else if (!state.preferences.consentGiven) {
            // If consent not given yet, show banner
            state.isBannerOpen = true;
          }
        }
      },
    }
  )
);
